// Operator › Platform › Guards (`/operator/guards`; MONITORING-P1, docs/design/2026-11-01-
// monitoring-in-companyos.md §4): the rules every DE's host telemetry is checked against. A rule
// matches a thread id (posted to or read), a tool name (a `*` pattern, e.g. `browser_*`) or a
// terminal call's sanitised argument summary (`*/tabs/*/evaluate`); it either opens an alert
// or stops the run and opens one. Built-in rules are marked and keep their pattern. A new rule
// always starts alert-only (the API's rule), so it can be watched before it stops anything; an
// edit raises it to Stop and alert, switches it off or changes its note. Every change is a new
// version in the platform audit. Platform admins only (the API's 403 is shown as a notice).
// Calls on load: `operator.guards.list`.
import type { OperationOutput } from "@companyos/sdk";
import { ApiError } from "@companyos/sdk";
import {
  Badge,
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  Field,
  FieldDescription,
  FieldError,
  FieldLabel,
  InfoTip,
  Input,
  LocationHeader,
  NativeSelect,
  NativeSelectOption,
  Switch,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Textarea,
} from "@companyos/ui";
import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PencilIcon, PlusIcon, ShieldIcon } from "lucide-react";
import { type FormEvent, useId, useState } from "react";
import type { Api } from "../../api/client";
import type { MeResponse } from "../../api/types";
import { formatDateTime } from "../format";
import { problemText } from "../ops-parts";
import { OpsFrame, operatorCrumb } from "../ops-ux";
import { LoadError, Loading } from "../screen-parts";

type Guard = OperationOutput<"operator.guards.list">["data"][number];
type Action = Guard["action"];
type MatchKind = Guard["match_kind"];

export const guardsKey = ["operator", "guards"] as const;
export const guardsQuery = (api: Api) =>
  queryOptions({ queryKey: guardsKey, queryFn: () => api.client.operator.guards.list() });

const ACTION_LABELS: Record<Action, string> = {
  alert: "Alert",
  stop_and_alert: "Stop and alert",
};
const MATCH_LABELS: Record<MatchKind, string> = {
  thread: "Thread",
  tool: "Tool",
  tool_arg: "Tool argument",
};
const PLACEHOLDERS: Record<MatchKind, string> = {
  tool: "browser_*",
  thread: "C05CLIENT-ANNOUNCE",
  tool_arg: "*/tabs/*/evaluate",
};
const HINTS: Record<MatchKind, string> = {
  tool: "A tool name; * matches any ending.",
  thread: "A thread or channel id, posted to or read; * matches any ending.",
  tool_arg:
    "A terminal call's command, URL host or path (no query or body); * matches anything, /…/ is a regex.",
};
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function GuardsPage({ api, me }: { api: Api; me: MeResponse }) {
  const guards = useQuery(guardsQuery(api));
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<Guard | null>(null);
  const forbidden = guards.error instanceof ApiError && guards.error.status === 403;
  const companies = new Map((me.agency?.grants ?? []).map((g) => [g.company_id, g.company_name]));
  const rows = guards.data?.data ?? [];
  return (
    <OpsFrame>
      <LocationHeader
        crumbs={[operatorCrumb, { label: "Guards" }]}
        icon={ShieldIcon}
        title="Guards"
        badge={
          <InfoTip label="About guards">
            Every event a DE's host reports is checked against these rules. A match opens an alert;
            a Stop and alert rule also stops the run on its host.
          </InfoTip>
        }
        actions={
          forbidden ? null : (
            <Button size="sm" onClick={() => setAdding(true)} data-slot="add-guard">
              <PlusIcon data-icon="inline-start" /> Rule
            </Button>
          )
        }
      />
      <div className="grid gap-3 px-6 py-4 max-md:px-4">
        {guards.isPending ? (
          <Loading label="Loading guards" />
        ) : forbidden ? (
          <EmptyState
            variant="card"
            icon={ShieldIcon}
            title="Platform admins only"
            description={problemText(
              guards.error,
              "Guards are managed by an admin of the platform's agency.",
            )}
          />
        ) : guards.isError ? (
          <LoadError what="Guards" error={guards.error} />
        ) : rows.length === 0 ? (
          <EmptyState
            variant="card"
            icon={ShieldIcon}
            title="No guard rules yet"
            description="Add a rule for a thread or a tool your agents must not touch."
          />
        ) : (
          <Table data-slot="guards">
            <TableCaption>Guard rules over DE telemetry.</TableCaption>
            <TableHeader>
              <TableRow>
                <TableHead scope="col">Match</TableHead>
                <TableHead scope="col">Applies to</TableHead>
                <TableHead scope="col">Action</TableHead>
                <TableHead scope="col">On</TableHead>
                <TableHead scope="col">Note</TableHead>
                <TableHead scope="col">Changed</TableHead>
                <TableHead scope="col">
                  <span className="sr-only">Edit</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((g) => (
                <GuardRow
                  key={g.id}
                  api={api}
                  g={g}
                  companies={companies}
                  onEdit={() => setEditing(g)}
                />
              ))}
            </TableBody>
          </Table>
        )}
      </div>
      {adding ? <AddGuardDialog api={api} me={me} onOpenChange={setAdding} /> : null}
      {editing ? (
        <EditGuardDialog
          api={api}
          g={editing}
          onOpenChange={(open) => {
            if (!open) setEditing(null);
          }}
        />
      ) : null}
    </OpsFrame>
  );
}

function scopeText(g: Guard, companies: Map<string, string>): string {
  if (!g.company_id) return "Every company";
  const name = companies.get(g.company_id) ?? "A company";
  return g.de_membership_id ? `${name} · one DE (${g.de_membership_id.slice(-8)})` : name;
}

function GuardRow({
  api,
  g,
  companies,
  onEdit,
}: {
  api: Api;
  g: Guard;
  companies: Map<string, string>;
  onEdit: () => void;
}) {
  const qc = useQueryClient();
  const toggle = useMutation({
    mutationFn: (enabled: boolean) =>
      api.client.operator.guards.update({ params: { id: g.id }, body: { enabled } }),
    onSettled: () => qc.invalidateQueries({ queryKey: guardsKey }),
  });
  return (
    <TableRow data-guard={g.id} data-builtin={g.builtin || undefined} data-enabled={g.enabled}>
      <TableHead scope="row" className="font-medium">
        <span className="inline-flex flex-wrap items-center gap-2">
          <Badge variant="outline">{MATCH_LABELS[g.match_kind]}</Badge>
          <code className="font-mono text-xs">{g.pattern}</code>
          {g.builtin ? <Badge variant="neutral">Built-in</Badge> : null}
        </span>
      </TableHead>
      <TableCell className="text-muted-foreground">{scopeText(g, companies)}</TableCell>
      <TableCell>
        <Badge variant={g.action === "stop_and_alert" ? "danger" : "warning"}>
          {ACTION_LABELS[g.action]}
        </Badge>
      </TableCell>
      <TableCell>
        <Switch
          aria-label={`On: ${g.pattern}`}
          checked={g.enabled}
          disabled={toggle.isPending}
          onCheckedChange={(v: boolean) => toggle.mutate(v)}
        />
      </TableCell>
      <TableCell className="whitespace-normal text-muted-foreground">{g.note ?? "—"}</TableCell>
      <TableCell className="text-muted-foreground tabular-nums">
        v{g.version} · {formatDateTime(g.updated_at)}
      </TableCell>
      <TableCell>
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label={`Edit ${g.pattern}`}
          title="Edit"
          onClick={onEdit}
        >
          <PencilIcon />
        </Button>
      </TableCell>
    </TableRow>
  );
}

function AddGuardDialog({
  api,
  me,
  onOpenChange,
}: {
  api: Api;
  me: MeResponse;
  onOpenChange: (open: boolean) => void;
}) {
  const qc = useQueryClient();
  const [kind, setKind] = useState<MatchKind>("tool");
  const [pattern, setPattern] = useState("");
  const [company, setCompany] = useState("");
  const [de, setDe] = useState("");
  const [note, setNote] = useState("");
  const ids = { kind: useId(), pattern: useId(), company: useId(), de: useId(), note: useId() };
  const p = pattern.trim();
  const deId = de.trim();
  const problem = /\s/.test(p)
    ? "No spaces in a pattern."
    : deId && !UUID.test(deId)
      ? "A DE id is a membership id (a UUID)."
      : deId && !company
        ? "A DE rule names its company."
        : null;
  const save = useMutation({
    mutationFn: () =>
      api.client.operator.guards.create({
        body: {
          match_kind: kind,
          pattern: p,
          ...(company ? { company_id: company } : {}),
          ...(deId ? { de_membership_id: deId } : {}),
          ...(note.trim() ? { note: note.trim() } : {}),
        },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: guardsKey });
      onOpenChange(false);
    },
  });
  const submit = (ev: FormEvent) => {
    ev.preventDefault();
    if (p && !problem) save.mutate();
  };
  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <form onSubmit={submit} className="grid gap-4" data-slot="add-guard-form">
          <DialogHeader>
            <DialogTitle>Add guard rule</DialogTitle>
            <DialogDescription>
              A new rule starts as alert only. Watch what it catches, then edit it to Stop and
              alert.
            </DialogDescription>
          </DialogHeader>
          <Field>
            <FieldLabel htmlFor={ids.kind}>Match</FieldLabel>
            <NativeSelect
              id={ids.kind}
              value={kind}
              onChange={(e) => setKind(e.target.value as MatchKind)}
            >
              <NativeSelectOption value="tool">Tool name</NativeSelectOption>
              <NativeSelectOption value="thread">Thread id</NativeSelectOption>
              <NativeSelectOption value="tool_arg">Tool argument</NativeSelectOption>
            </NativeSelect>
          </Field>
          <Field>
            <FieldLabel htmlFor={ids.pattern}>Pattern</FieldLabel>
            <Input
              id={ids.pattern}
              value={pattern}
              onChange={(e) => setPattern(e.target.value)}
              placeholder={PLACEHOLDERS[kind]}
              autoComplete="off"
              spellCheck={false}
              maxLength={200}
            />
            <FieldDescription>{HINTS[kind]}</FieldDescription>
          </Field>
          <Field>
            <FieldLabel htmlFor={ids.company}>Company</FieldLabel>
            <NativeSelect
              id={ids.company}
              value={company}
              onChange={(e) => setCompany(e.target.value)}
            >
              <NativeSelectOption value="">Every company</NativeSelectOption>
              {(me.agency?.grants ?? []).map((g) => (
                <NativeSelectOption key={g.company_id} value={g.company_id}>
                  {g.company_name}
                </NativeSelectOption>
              ))}
            </NativeSelect>
          </Field>
          <Field>
            <FieldLabel htmlFor={ids.de}>One DE only (optional)</FieldLabel>
            <Input
              id={ids.de}
              value={de}
              onChange={(e) => setDe(e.target.value)}
              placeholder="DE membership id"
              autoComplete="off"
              spellCheck={false}
            />
          </Field>
          <Field>
            <FieldLabel htmlFor={ids.note}>Note (optional)</FieldLabel>
            <Textarea
              id={ids.note}
              value={note}
              maxLength={300}
              rows={2}
              onChange={(e) => setNote(e.target.value)}
            />
          </Field>
          {problem && p ? <FieldError>{problem}</FieldError> : null}
          {save.error ? (
            <FieldError>{problemText(save.error, "The rule could not be added.")}</FieldError>
          ) : null}
          <DialogFooter>
            <DialogClose render={<Button variant="secondary" type="button" />}>Cancel</DialogClose>
            <Button type="submit" disabled={save.isPending || !p || Boolean(problem)}>
              {save.isPending ? "Adding…" : "Add"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function EditGuardDialog({
  api,
  g,
  onOpenChange,
}: {
  api: Api;
  g: Guard;
  onOpenChange: (open: boolean) => void;
}) {
  const qc = useQueryClient();
  const [pattern, setPattern] = useState(g.pattern);
  const [action, setAction] = useState<Action>(g.action);
  const [enabled, setEnabled] = useState(g.enabled);
  const [note, setNote] = useState(g.note ?? "");
  const ids = { pattern: useId(), action: useId(), enabled: useId(), note: useId() };
  const p = pattern.trim();
  const body = {
    ...(!g.builtin && p !== g.pattern ? { pattern: p } : {}),
    ...(action !== g.action ? { action } : {}),
    ...(enabled !== g.enabled ? { enabled } : {}),
    ...(note.trim() !== (g.note ?? "") ? { note: note.trim() || null } : {}),
  };
  const changed = Object.keys(body).length > 0;
  const bad = !p || /\s/.test(p);
  const save = useMutation({
    mutationFn: () => api.client.operator.guards.update({ params: { id: g.id }, body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: guardsKey });
      onOpenChange(false);
    },
  });
  const submit = (ev: FormEvent) => {
    ev.preventDefault();
    if (changed && !bad) save.mutate();
  };
  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <form onSubmit={submit} className="grid gap-4" data-slot="edit-guard-form">
          <DialogHeader>
            <DialogTitle>Edit guard rule</DialogTitle>
            <DialogDescription>
              {MATCH_LABELS[g.match_kind]} rule, version {g.version}. Saving makes a new version.
            </DialogDescription>
          </DialogHeader>
          <Field>
            <FieldLabel htmlFor={ids.pattern}>Pattern</FieldLabel>
            <Input
              id={ids.pattern}
              value={pattern}
              onChange={(e) => setPattern(e.target.value)}
              disabled={g.builtin}
              autoComplete="off"
              spellCheck={false}
              maxLength={200}
            />
            {g.builtin ? (
              <FieldDescription>A built-in rule keeps its pattern.</FieldDescription>
            ) : null}
          </Field>
          <Field>
            <FieldLabel htmlFor={ids.action}>Action</FieldLabel>
            <NativeSelect
              id={ids.action}
              value={action}
              onChange={(e) => setAction(e.target.value as Action)}
            >
              <NativeSelectOption value="alert">Alert</NativeSelectOption>
              <NativeSelectOption value="stop_and_alert">Stop and alert</NativeSelectOption>
            </NativeSelect>
            <FieldDescription>
              Stop and alert also asks the DE's bridge to stop the run on its host.
            </FieldDescription>
          </Field>
          <Field orientation="horizontal">
            <Switch id={ids.enabled} checked={enabled} onCheckedChange={setEnabled} />
            <FieldLabel htmlFor={ids.enabled}>On</FieldLabel>
          </Field>
          <Field>
            <FieldLabel htmlFor={ids.note}>Note</FieldLabel>
            <Textarea
              id={ids.note}
              value={note}
              maxLength={300}
              rows={2}
              onChange={(e) => setNote(e.target.value)}
            />
          </Field>
          {save.error ? (
            <FieldError>{problemText(save.error, "The rule could not be saved.")}</FieldError>
          ) : null}
          <DialogFooter>
            <DialogClose render={<Button variant="secondary" type="button" />}>Cancel</DialogClose>
            <Button type="submit" disabled={save.isPending || !changed || bad}>
              {save.isPending ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
